/**
 * create_tester_note Tool — create a new tester knowledge-base note (Issue #2661)
 *
 * Returns the new note's ID so the caller can chain into update_tester_note or
 * delete_tester_note without an extra list_tester_notes round trip.
 */

import type { Tool } from '@modelcontextprotocol/sdk/types.js';
import type { JobClient } from '../clients/job-client.js';
import type { CreateTesterNoteRequest, TesterNoteTag } from '@runhuman/shared';

const NOTE_TAGS: readonly TesterNoteTag[] = [
  'credentials',
  'environment',
  'setup',
  'procedures',
  'known-issues',
  'contacts',
  'general',
];

export type CreateTesterNoteArgs = CreateTesterNoteRequest;

export const createTesterNoteToolDefinition: Tool = {
  name: 'create_tester_note',
  description: `Create a tester knowledge-base note.

Notes hold persistent context for human testers (credentials, environment, procedures, known issues) and are scoped to an organization, optionally narrowed to a single project.

Requires the caller's PAT to be a Volter-staff token or an admin of the target org. The org also needs an active subscription unless the caller is staff.`,
  inputSchema: {
    type: 'object',
    properties: {
      title: {
        type: 'string',
        description: 'Short note title (required).',
      },
      body: {
        type: 'string',
        description: 'Full note body — supports plain text or Markdown (required).',
      },
      organizationId: {
        type: 'string',
        description: 'Organization the note belongs to (required).',
      },
      projectId: {
        type: ['string', 'null'],
        description: 'Project to scope the note to. Omit or pass null for an org-wide note.',
      },
      tags: {
        type: 'array',
        items: { type: 'string', enum: NOTE_TAGS as unknown as string[] },
        description: `Optional tags. Allowed: ${NOTE_TAGS.join(', ')}.`,
      },
      isInternal: {
        type: 'boolean',
        description: 'Mark internal-only (Volter staff only). Non-staff callers must omit or pass false.',
      },
    },
    required: ['title', 'body', 'organizationId'],
  },
};

export async function handleCreateTesterNote(
  args: CreateTesterNoteArgs,
  jobClient: JobClient,
): Promise<{ content: Array<{ type: 'text'; text: string }>; isError?: boolean }> {
  try {
    const note = await jobClient.createTesterNote(args);

    return {
      content: [{
        type: 'text',
        text: `Tester Note Created!

Title: ${note.title}
Note ID: ${note.id}
Org: ${note.organizationName} (${note.organizationId})
Project: ${note.projectId ? `${note.projectName ?? note.projectId} (${note.projectId})` : 'org-wide (no project)'}
Visibility: ${note.isInternal ? 'internal' : 'customer-visible'}${note.tags.length > 0 ? `\nTags: ${note.tags.join(', ')}` : ''}

Use this ID to chain follow-ups:
- update_tester_note(noteId: "${note.id}", ...)
- delete_tester_note(noteId: "${note.id}")`,
      }],
    };
  } catch (error) {
    const err = error as Error & { status?: number };
    return {
      content: [{
        type: 'text',
        text: `Failed to create tester note\n\nError: ${err.message}${err.status ? `\nStatus: ${err.status}` : ''}`,
      }],
      isError: true,
    };
  }
}
